import { camelizeKeys } from "humps";
import pick from "lodash/pick";
import { Activity, Id, Profile, PureCustomValueCamelCased } from "PFCore/types";
import { Option } from "PFTypes/options";

export const mapTagToOption = (tag: PureCustomValueCamelCased): Option<PureCustomValueCamelCased> => ({
  id: tag.id,
  value: String(tag.text),
  original: tag,
  displayElement: tag.text
});

export enum ManageTagsModalAction {
  Create = "create",
  Update = "update",
  Delete = "delete"
}

export enum RecipientRoleKey {
  PrincipalOwner = "principalOwner",
  Owner = "owner",
  Assignee = "assignee",
  Reviewer = "reviewer"
}

export type RecipientUser = {
  id: Id;
  firstName: string | undefined;
  lastName: string | null | undefined;
  email: string;
  role: RecipientRoleKey[];
};

const getRoleHierarchyValue = (role: RecipientRoleKey): number => {
  switch (role) {
    case RecipientRoleKey.PrincipalOwner:
      return 1;
    case RecipientRoleKey.Owner:
      return 2;
    case RecipientRoleKey.Assignee:
      return 3;
    case RecipientRoleKey.Reviewer:
      return 4;
    default:
      return 999;
  }
};

export const extractRecipientUsers = (activity: Activity | undefined): RecipientUser[] => {
  if (!activity) {
    return [];
  }

  const recipientsMap = new Map<Id, RecipientUser>();

  const addOrUpdateRecipient = (profile: Profile, role: RecipientRoleKey) => {
    const recipient = {
      ...(pick(camelizeKeys(profile), "id", "firstName", "lastName", "email") as RecipientUser),
      role: [role]
    };
    const existingRecipient = recipientsMap.get(recipient.id);

    if (!existingRecipient) {
      recipientsMap.set(recipient.id, recipient);
    } else {
      if (!existingRecipient.role.includes(role)) {
        existingRecipient.role.push(role);
        existingRecipient.role.sort(
          (roleA, roleB) => getRoleHierarchyValue(roleA) - getRoleHierarchyValue(roleB)
        );
      }
    }
  };

  activity.owners.forEach((owner) => {
    const role = owner.principal ? RecipientRoleKey.PrincipalOwner : RecipientRoleKey.Owner;
    addOrUpdateRecipient(owner.profile, role);
  });

  activity.reviewers.forEach((reviewer) => {
    addOrUpdateRecipient(reviewer.profile, RecipientRoleKey.Reviewer);
  });

  if (activity.assignee) {
    addOrUpdateRecipient(activity.assignee.profile, RecipientRoleKey.Assignee);
  }

  return Array.from(recipientsMap.values()).sort(
    (recipientA, recipientB) =>
      getRoleHierarchyValue(recipientA.role[0]) - getRoleHierarchyValue(recipientB.role[0])
  );
};
